import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, MessageCircle, Send } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { postContact } from '../../api';
import { CONTACT_INFO } from '../../data/content';
import Reveal from './Reveal';

const EMPTY_FORM = {
  name: '',
  cpfCnpj: '',
  address: '',
  city: '',
  state: '',
  phone: '',
  email: '',
  service: '',
  message: '',
};

const inputClass =
  'bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400/60 h-12';

const ContactSection = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setFormData((prev) => ({ ...prev, service: e.detail || '' }));
    window.addEventListener('prefill-service', onPrefill);
    return () => window.removeEventListener('prefill-service', onPrefill);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postContact({
        name: formData.name,
        cpf_cnpj: formData.cpfCnpj,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        phone: formData.phone,
        email: formData.email || undefined,
        service: formData.service || undefined,
        message: formData.message || undefined,
      });
      toast.success('Mensagem enviada!', {
        description: 'Entraremos em contato em breve.',
      });
      setFormData(EMPTY_FORM);
    } catch (err) {
      toast.error('Não foi possível enviar', {
        description: 'Tente novamente ou fale conosco pelo WhatsApp.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contato-form" data-testid="contact-section" className="relative py-28 bg-[#061226] overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-[34rem] h-[34rem] spotlight-yellow opacity-40 pointer-events-none" />
      <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-5 gap-12 items-start">
        <Reveal className="lg:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-300 mb-4">
            Contato
          </p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Fale Conosco
          </h2>
          <p className="mt-5 text-slate-400 text-sm sm:text-base leading-relaxed">
            Solicite um orçamento, um projeto ou a agenda de treinamentos.
            Preencha o formulário ou chame direto pelos nossos canais.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="whatsapp-cta"
              href={`https://wa.me/${CONTACT_INFO.whatsappNumber}`}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 hover:border-emerald-400/50 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-emerald-400" />
              </span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  WhatsApp
                </span>
                <span className="block font-display font-bold text-white group-hover:text-emerald-300 transition-colors">
                  {CONTACT_INFO.whatsappDisplay}
                </span>
              </span>
            </a>
            <a
              data-testid="email-cta"
              href={`mailto:${CONTACT_INFO.email}`}
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 hover:border-amber-400/50 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-amber-400" />
              </span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  E-mail
                </span>
                <span className="block font-display font-bold text-white group-hover:text-amber-300 transition-colors break-all">
                  {CONTACT_INFO.email}
                </span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 sm:p-9 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="contact-name" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  Nome / Razão Social *
                </Label>
                <Input id="contact-name" data-testid="contact-name-input" name="name" value={formData.name} onChange={handleChange} required className={`${inputClass} mt-2`} />
              </div>
              <div>
                <Label htmlFor="contact-cpfcnpj" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  CPF / CNPJ *
                </Label>
                <Input id="contact-cpfcnpj" data-testid="contact-cpfcnpj-input" name="cpfCnpj" value={formData.cpfCnpj} onChange={handleChange} required placeholder="000.000.000-00" className={`${inputClass} mt-2`} />
              </div>
            </div>

            <div>
              <Label htmlFor="contact-address" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                Endereço Completo *
              </Label>
              <Input id="contact-address" data-testid="contact-address-input" name="address" value={formData.address} onChange={handleChange} required placeholder="Rua, número, complemento" className={`${inputClass} mt-2`} />
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <div className="sm:col-span-2">
                <Label htmlFor="contact-city" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  Cidade *
                </Label>
                <Input id="contact-city" data-testid="contact-city-input" name="city" value={formData.city} onChange={handleChange} required className={`${inputClass} mt-2`} />
              </div>
              <div>
                <Label htmlFor="contact-state" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  Estado *
                </Label>
                <Input id="contact-state" data-testid="contact-state-input" name="state" value={formData.state} onChange={handleChange} required placeholder="BA" maxLength={2} className={`${inputClass} mt-2`} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="contact-phone" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  Telefone *
                </Label>
                <Input id="contact-phone" data-testid="contact-phone-input" name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="(00) 00000-0000" className={`${inputClass} mt-2`} />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                  E-mail
                </Label>
                <Input id="contact-email" data-testid="contact-email-input" name="email" type="email" value={formData.email} onChange={handleChange} className={`${inputClass} mt-2`} />
              </div>
            </div>

            <div>
              <Label htmlFor="contact-service" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                Serviço de Interesse
              </Label>
              <Input id="contact-service" data-testid="contact-service-input" name="service" value={formData.service} onChange={handleChange} placeholder="Ex: Projeto de Incêndio, Treinamento Básico..." className={`${inputClass} mt-2`} />
            </div>

            <div>
              <Label htmlFor="contact-message" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
                Mensagem
              </Label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Descreva sua necessidade..."
                className="mt-2 w-full min-h-28 rounded-md bg-white/5 border border-white/10 text-white placeholder:text-slate-500 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-form-submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full hover:bg-amber-300 hover:shadow-[0_0_32px_rgba(252,211,77,0.4)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Enviando...' : 'Enviar Mensagem'}
              <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
